import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ChangeEvent,
} from 'react'
import { Mic, Loader2, Camera, X, RotateCw, AlertTriangle, Images as ImagesIcon } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import useKeyboardInset from '../hooks/useKeyboardInset'
import { textToNoteHtml } from '../utils/noteHtml'

/** One attached photo, as the composer needs to draw it. */
export interface ComposerPhoto {
	id: string
	/** An object URL for the local file — shown before any upload finishes. */
	previewUrl: string
	status: 'ready' | 'uploading' | 'uploaded' | 'failed'
	error?: string
}

export interface FieldNoteComposerProps {
	/**
	 * Receives the note body as HTML, already `<p>`-wrapped and escaped.
	 * Throwing or rejecting leaves the text in place; see `error`.
	 */
	onSubmit: (html: string) => void | Promise<void>
	onCancel?: () => void
	placeholder?: string
	submitLabel?: string
	/** Shown under the field. The caller owns the failure message. */
	error?: string | null
	/** Disables the controls while the caller is saving. */
	busy?: boolean
	/**
	 * Photos attached to this note. Owned by the caller, because the caller is
	 * what uploads them and therefore what knows how each one is getting on.
	 */
	photos?: ComposerPhoto[]
	onAddPhotos?: (files: File[]) => void
	/**
	 * Offer the field image library alongside the camera.
	 *
	 * Optional, so a caller with no library context (chat, say) still gets the
	 * plain camera button rather than a chooser whose second option goes
	 * nowhere.
	 */
	onPickFromLibrary?: () => void
	onRemovePhoto?: (id: string) => void
	onRetryPhoto?: (id: string) => void
	autoFocus?: boolean
	className?: string
}

const MIN_ROWS_PX = 96
const MAX_HEIGHT_PX = 320

/**
 * The field note composer: a plain `<textarea>`, deliberately.
 *
 * **This exists because of dictation.** Voice notes were the requirement, and
 * the decision was to use the keyboard's own mic rather than ship a recorder —
 * it is on-device, needs no permission of ours, costs nothing, survives poor
 * signal, and works identically in Safari and Firefox on iOS because it is a
 * keyboard feature, not a browser one.
 *
 * The obstacle was never voice. It is `react-quill`: its contenteditable is
 * exactly where iOS dictation and IME input misbehave — caret jumps, dropped
 * input, the toolbar stealing focus mid-utterance. A textarea has none of that,
 * so dictation works with no code at all.
 *
 * The desktop Quill composer (`NoteAddCN`) is untouched. This is an additional
 * composer for field screens, not a replacement — Quill is used across a lot of
 * desktop surfaces and rewriting them is not this change.
 */
export function FieldNoteComposer({
	onSubmit,
	onCancel,
	placeholder = 'What did you see?',
	submitLabel = 'Save note',
	error = null,
	busy = false,
	photos = [],
	onAddPhotos,
	onPickFromLibrary,
	onRemovePhoto,
	onRetryPhoto,
	autoFocus = false,
	className,
}: FieldNoteComposerProps) {
	const [text, setText] = useState('')
	const textareaRef = useRef<HTMLTextAreaElement | null>(null)
	const cameraRef = useRef<HTMLInputElement | null>(null)
	/** Open while the user decides between the camera and the library. */
	const [choosing, setChoosing] = useState(false)
	const keyboardInset = useKeyboardInset()

	// Grow with the content instead of scrolling a four-line box. Someone
	// dictating a paragraph should be able to see the paragraph.
	useLayoutEffect(() => {
		const el = textareaRef.current
		if (!el) return
		el.style.height = 'auto'
		el.style.height = `${Math.min(Math.max(el.scrollHeight, MIN_ROWS_PX), MAX_HEIGHT_PX)}px`
	}, [text])

	useEffect(() => {
		if (autoFocus) textareaRef.current?.focus()
	}, [autoFocus])

	const handleSubmit = useCallback(async () => {
		const html = textToNoteHtml(text)
		// A note that is only photographs is common in the field — six pictures
		// of a pylon sign and nothing written. Requiring text would block it.
		if ((!html && photos.length === 0) || busy) return
		try {
			await onSubmit(html)
		} catch {
			// Swallowed on purpose. The caller owns the failure — it knows what
			// went wrong and passes it back through `error`. Letting it escape
			// here buys nothing and raises an unhandled rejection instead. The
			// text stays put either way, which is the part that matters when the
			// note was dictated in a car park.
		}
	}, [text, busy, photos.length, onSubmit])

	const pickPhotos = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(event.target.files ?? [])
			if (files.length) onAddPhotos?.(files)
			// Cleared so picking the SAME file twice still fires a change event —
			// which, taking near-identical photos of one storefront, happens.
			event.target.value = ''
		},
		[onAddPhotos],
	)

	const hasText = Boolean(text.trim())
	const canSubmit = hasText || photos.length > 0

	return (
		<div
			className={cn('bg-white border-t border-gray-200', className)}
			// Lift clear of the on-screen keyboard. iOS draws it over the page
			// rather than resizing it, so without this the composer and its Save
			// button sit underneath the keyboard the user is typing on.
			style={{
				paddingBottom: keyboardInset
					? `${keyboardInset}px`
					: 'max(1rem, env(safe-area-inset-bottom))',
			}}
		>
			<div className="p-4 space-y-3">
				<textarea
					ref={textareaRef}
					value={text}
					onChange={e => setText(e.target.value)}
					placeholder={placeholder}
					disabled={busy}
					className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-base leading-relaxed"
					// 16px or larger, or iOS zooms the whole page on focus and the
					// user is left pinching their way back out.
					style={{ fontSize: '16px', minHeight: MIN_ROWS_PX }}
					// Prose, not an identifier: the corrections and capitals are
					// wanted here.
					autoCapitalize="sentences"
					autoCorrect="on"
					spellCheck
				/>

				{!hasText && (
					<p className="flex items-center gap-1.5 text-xs text-gray-400">
						<Mic className="w-3.5 h-3.5" />
						Tap the mic on your keyboard to dictate instead of typing.
					</p>
				)}

				{photos.length > 0 && (
					<ul className="flex gap-2 overflow-x-auto pb-1">
						{photos.map(photo => (
							<li key={photo.id} className="relative shrink-0">
								<img
									src={photo.previewUrl}
									alt=""
									className="w-20 h-20 rounded-md object-cover bg-gray-100"
								/>

								{photo.status === 'uploading' && (
									<span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
										<Loader2 className="w-5 h-5 animate-spin text-white" />
									</span>
								)}

								{photo.status === 'failed' && (
									<button
										type="button"
										onClick={() => onRetryPhoto?.(photo.id)}
										aria-label={`Retry this photo — ${photo.error ?? 'upload failed'}`}
										className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded-md bg-destructive/80 text-white"
									>
										<AlertTriangle className="w-4 h-4" />
										<RotateCw className="w-3.5 h-3.5" />
									</button>
								)}

								{onRemovePhoto && photo.status !== 'uploading' && (
									<button
										type="button"
										onClick={() => onRemovePhoto(photo.id)}
										aria-label="Remove this photo"
										className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 text-white shadow"
									>
										<X className="w-3.5 h-3.5" />
									</button>
								)}
							</li>
						))}
					</ul>
				)}

				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}

				{onAddPhotos && (
					<>
						{/*
						 * A file input with `capture`, not getUserMedia. It opens the
						 * system camera the user already knows, needs no permission of
						 * ours, and — unlike GPS — is NOT a secure-context API, so it
						 * works over plain HTTP on a LAN address too.
						 */}
						<input
							ref={cameraRef}
							type="file"
							accept="image/*"
							capture="environment"
							multiple
							onChange={pickPhotos}
							className="hidden"
							data-testid="field-camera-input"
						/>
						{/*
							With a library available this becomes a chooser rather than a
							shortcut to the camera. Without one it stays exactly what it
							was: a caller with no library context should not be offered a
							second option that goes nowhere.
						*/}
						<button
							type="button"
							onClick={() => (onPickFromLibrary ? setChoosing(true) : cameraRef.current?.click())}
							disabled={busy}
							className="w-full inline-flex items-center justify-center gap-2 min-h-[2.75rem] rounded-md border border-gray-300 text-sm font-medium text-gray-700"
						>
							<Camera className="w-4 h-4" />
							{photos.length ? 'Add another photo' : 'Add a photo'}
						</button>

						<Dialog open={choosing} onClose={() => setChoosing(false)}>
							<DialogContent className="max-w-xs">
								<DialogHeader>
									<DialogTitle>Add a photo</DialogTitle>
									<DialogDescription>Take one now, or use one you already shot.</DialogDescription>
								</DialogHeader>
								<div className="space-y-2 p-5 pt-2">
									<Button
										variant="primary"
										isInput
										className="w-full"
										onClick={() => {
											setChoosing(false)
											cameraRef.current?.click()
										}}
									>
										<Camera className="mr-2 h-4 w-4" />
										Capture New Image
									</Button>
									<Button
										variant="ghost"
										isInput
										className="w-full"
										onClick={() => {
											setChoosing(false)
											onPickFromLibrary?.()
										}}
									>
										<ImagesIcon className="mr-2 h-4 w-4" />
										Pull from Image Library
									</Button>
								</div>
							</DialogContent>
						</Dialog>
					</>
				)}

				<div className="flex gap-2">
					{onCancel && (
						<Button variant="ghost" isInput onClick={onCancel} disabled={busy} className="flex-1">
							Cancel
						</Button>
					)}
					<Button
						variant="primary"
						isInput
						onClick={handleSubmit}
						disabled={!canSubmit || busy}
						className="flex-1"
					>
						{busy ? (
							<>
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								Saving…
							</>
						) : (
							submitLabel
						)}
					</Button>
				</div>
			</div>
		</div>
	)
}

export default FieldNoteComposer
